<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>仮想ネットワーク一覧</span>
        <manual-button path="/virtual-nw-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <v-row no-gutters>
          <v-col>
            <app-search
              :search="networkLine.search"
              v-model="networkLine.filter"
            />
          </v-col>
          <div class="d-flex flex-row ga-2 flex-wrap ml-3">
            <v-btn
              v-permission.config
              color="primary"
              @click="addVirtualNetwork"
              >追加</v-btn
            >
            <v-btn
              v-permission.config
              color="primary"
              @click="deleteVirtualNetwork"
              >削除</v-btn
            >
            <v-btn color="primary" @click="downloadCsv">一覧ダウンロード</v-btn>
          </div>
        </v-row>
        <v-alert
          v-if="alertMsg"
          class="mt-2 mb-2"
          density="compact"
          :text="alertMsg"
        ></v-alert>
        <div class="d-flex flex-row justify-end mb-2">
          <v-btn color="primary" @click="load">一覧更新</v-btn>
        </div>
        <app-table
          v-model:table-sort-by="networkLine.sortBy"
          primary-key="vnCode"
          :selectable="selectable"
          :fields="networkLine.fields"
          :items="vnetwork.vnInfoList"
          :filter-option="networkLine.filter_option"
          :filter="networkLine.filter"
          v-model="networkLine.selected"
          :hover="true"
          sticky-header="50vh"
          @click:row="showVNReference"
        >
          <template #cell(vlan)="{ value }">
            {{ $filter.emptyTo(value, "-") }}
          </template>
        </app-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import VirtualNetworkReference from "@/modals/virtualNetwork/VirtualNetworkReference.vue";
import VirtualNetworkEdit from "@/modals/virtualNetwork/VirtualNetworkEdit.vue";
import type {
  VirtualNetworkEntity,
  VirtualNetworkGetEntity,
} from "@/apis/VirtualNetworkApi";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";

export default defineComponent({
  name: "VirtualNetworkList",
  data() {
    return {
      alertMsg: null as string | null,
      networkLine: {
        sortBy: {
          key: "vnCode",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "vnName",
            title: "仮想ネットワーク(VN)名",
            sortable: true,
            width: "250px",
          },
          {
            key: "vnCode",
            title: "VNコード",
            sortable: true,
            width: "170px",
          },
          {
            key: "vnType",
            title: "L2/L3",
            sortable: true,
            width: "170px",
          },
          {
            key: "vlan",
            title: "VLAN",
            sortable: true,
            width: "170px",
            align: "end",
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (_v: string, _k: string, item: VirtualNetworkEntity) => {
              if (item.vlanIdType === null) {
                return null;
              } else if (item.vlanIdType === "SPECIFIED") {
                return `${item.vlanId}`;
              } else {
                return this.$filter.enumTo(item.vlanIdType, "vlanIdType");
              }
            },
          },
          {
            key: "numberOfVnConnect",
            title: "VNコネクト数(登録数)",
            align: "end",
            width: "250px",
          },
          {
            key: "upperLimitVnConnect",
            title: "VNコネクト数(上限数)",
            align: "end",
            width: "250px",
          },
        ] as AppTableHeader<VirtualNetworkEntity>[],
        selected: [] as VirtualNetworkEntity[],
        search: {
          vnName: { type: "text", label: "仮想ネットワーク(VN)名" },
          vnCode: { type: "text", label: "VNコード" },
          vnType: {
            type: "select",
            label: "L2/L3",
            items: [
              { value: "L2", text: "L2" },
              { value: "L3", text: "L3" },
            ],
          },
          vlan: { type: "text", label: "VLAN" },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          vnName: "like",
          vnCode: "like",
          vnType: "equal",
          vlan: "like",
        } as FilterOption,
      },
      vnetwork: {} as VirtualNetworkGetEntity,
      isLoaded: false,
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** 一覧を取得 */
    async load() {
      this.alertMsg = null;
      this.vnetwork = await this.$api.virtualNetwork.getAll();
    },
    /** 追加 */
    async addVirtualNetwork() {
      this.alertMsg = null;
      await this.$modal.show(VirtualNetworkEdit, {
        vnKeyId: this.vnetwork.vnKeyId,
      });
      await this.load();
    },
    /** 削除 */
    async deleteVirtualNetwork() {
      if (this.networkLine.selected.length > 0) {
        this.alertMsg = null;
        await this.$confirm("仮想ネットワークを削除します。よろしいですか？");
        await this.$api.virtualNetwork.delete({
          vnCodeList: this.networkLine.selected.map((e) => e.vnCode),
          vnKeyId: this.vnetwork.vnKeyId,
        });
        await this.load();
      } else {
        this.alertMsg = this.$filter.message("no_select_data");
      }
    },
    /** 詳細*/
    async showVNReference(entity: VirtualNetworkEntity) {
      this.alertMsg = null;
      await this.$modal.show(VirtualNetworkReference, {
        network: entity,
        vnKeyId: this.vnetwork.vnKeyId,
      });
      await this.load();
    },
    /** フロント側のみでのCSVダウンロード */
    downloadCsv() {
      this.$service.csvService.appTableCsvDownLoad(
        "virtual_network_setting",
        this.vnetwork.vnInfoList.sortBy(["vnCode", "desc"]),
        this.$service.csvService.genFieldList(this.networkLine.fields)
      );
    },
  },
});
</script>
